import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, Loader2, MonitorPlay, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/pulse-settings`;
const SPEEDS = [5, 8, 10, 15, 20, 30, 45, 60];

const StaffRow = ({ s, onToggle }) => (
  <div className="flex items-center gap-3 py-2.5 border-b last:border-0" data-testid={`pulse-settings-staff-${s.id}`}>
    <div className="flex-1 min-w-0">
      <div className="font-medium truncate">{s.name}</div>
      <div className="text-xs text-muted-foreground truncate">{s.email}{s.role ? ` · ${s.role.replace(/_/g, ' ')}` : ''}</div>
    </div>
    <span className={`text-xs ${s.shown ? 'text-emerald-700' : 'text-muted-foreground'}`}>{s.shown ? 'Shown' : 'Hidden'}</span>
    <Switch checked={s.shown} onCheckedChange={onToggle} aria-label={`Show ${s.name} on the TV`} data-testid={`pulse-settings-staff-switch-${s.id}`} />
  </div>
);

export default function PulseSettingsPage() {
  const { user } = useAuth();
  const [rotate, setRotate] = useState(8);
  const [staff, setStaff] = useState(null);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === 'system_admin';

  const apply = (d) => { setRotate(d.rotate_seconds); setStaff(d.staff); };
  useEffect(() => { if (isAdmin) axios.get(API).then((r) => apply(r.data)).catch(() => toast.error('Failed to load Team Pulse settings')); }, [isAdmin]);

  const shown = useMemo(() => (staff || []).filter((s) => s.name.toLowerCase().includes(q.toLowerCase()) || (s.email || '').toLowerCase().includes(q.toLowerCase())), [staff, q]);
  const setAll = (v) => setStaff(staff.map((s) => (shown.some((x) => x.id === s.id) ? { ...s, shown: v } : s)));
  const save = async () => {
    setSaving(true);
    try {
      apply((await axios.put(API, { rotate_seconds: rotate, hidden_user_ids: staff.filter((s) => !s.shown).map((s) => s.id) })).data);
      toast.success('Team Pulse settings saved — the TV updates within 30 seconds');
    } catch { toast.error('Could not save Team Pulse settings'); } finally { setSaving(false); }
  };

  if (!isAdmin) return <p className="text-muted-foreground" data-testid="pulse-settings-no-access">Only System Administrators can manage Team Pulse.</p>;
  if (!staff) return <Loader2 className="h-5 w-5 animate-spin" />;
  const count = staff.filter((s) => s.shown).length;
  return (
    <div className="space-y-6 max-w-3xl" data-testid="pulse-settings-page">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2"><MonitorPlay className="h-6 w-6" />Team Pulse TV</h1>
          <p className="text-muted-foreground text-sm">Choose who appears on the management TV and how fast the staff circles rotate.</p>
        </div>
        <Button variant="outline" asChild data-testid="pulse-settings-open-tv"><a href="/pulse" target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Open TV view</a></Button>
      </div>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Circle rotation</CardTitle><CardDescription>Staff Activity shows 5 people at a time, then moves to the next 5.</CardDescription></CardHeader>
        <CardContent className="flex items-center gap-3">
          <Label htmlFor="pulse-rotate">Show each group for</Label>
          <Select value={String(rotate)} onValueChange={(v) => setRotate(Number(v))}>
            <SelectTrigger id="pulse-rotate" className="w-40" data-testid="pulse-settings-rotate-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SPEEDS.map((s) => <SelectItem key={s} value={String(s)} data-testid={`pulse-settings-rotate-${s}`}>{s} seconds</SelectItem>)}</SelectContent>
          </Select>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Staff on the TV <span className="text-muted-foreground font-normal" data-testid="pulse-settings-count">({count} of {staff.length} shown)</span></CardTitle>
          <CardDescription>Hidden staff are left out of the circles, activity stream, Active Staff count, Caseload Review and Task Status. New staff accounts show automatically.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff" className="pl-9" data-testid="pulse-settings-search" /></div>
            <Button variant="outline" onClick={() => setAll(true)} data-testid="pulse-settings-show-all">Show all</Button>
            <Button variant="outline" onClick={() => setAll(false)} data-testid="pulse-settings-hide-all">Hide all</Button>
          </div>
          <div className="max-h-[420px] overflow-y-auto pr-1">
            {shown.map((s) => <StaffRow key={s.id} s={s} onToggle={(v) => setStaff(staff.map((x) => (x.id === s.id ? { ...x, shown: v } : x)))} />)}
            {shown.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No staff match your search.</p>}
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="pulse-settings-save-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button></div>
    </div>
  );
}
